/**
 * batch-69 magazijn-cijfers (FO-520 §11) — de pure regels van de telsessie:
 * het verschil en de correctie, de vier-ogen-drempel, de looproute-volgorde en
 * de TEL-nummering. Geen database, dus los te testen; de API en het scherm
 * rekenen met dezelfde functies (één waarheid).
 */

import { compareVaklocatie } from './vaklocatie'
import { formatEuroString, geldNaarEenheden, vergelijkGeld, vermenigvuldigGeld } from './besteladvies'

// ─── Verschil en correctie ──────────────────────────────────────────────────

export interface TellingVerschilInput {
  /** Het saldo bij de snapshot (het aanmaakmoment van de telling). */
  systeemSnapshot: number
  /** Het saldo van NU (de snapshot plus alles wat sindsdien geboekt is). */
  huidigSaldo: number
  /**
   * Het saldo op het moment van tellen (vastgelegd bij tellen, scannen of
   * importeren). Null of weggelaten = onbekend: dan rekent de correctie met het
   * huidige saldo.
   */
  saldoBijTellen?: number | null
  /** Het getelde aantal; null = nog niet geteld (niet 0). */
  geteld: number | null
}

export interface TellingVerschil {
  geteld: boolean
  /** Geteld min de snapshot. */
  verschil: number | null
  /**
   * Wat er geboekt wordt: geteld min het saldo OP HET MOMENT VAN TELLEN (spec
   * §C14: de teller telde wat er toen lag). Een levering of uitgifte tussen de
   * snapshot en het tellen zit al in dat saldo; een beweging ná het tellen is
   * een echte beweging en blijft buiten de correctie, ook als het doorvoeren of
   * de tweede goedkeuring pas dagen later komt.
   */
  correctieAantal: number | null
  /** Het saldo waartegen de correctie rekent: bij tellen, of nu als dat onbekend is. */
  peilSaldo: number
  saldoGewijzigdSindsSnapshot: boolean
  /** Peilsaldo min snapshot: wat er tussen de snapshot en het tellen geboekt is (+ of -). */
  mutatiesSindsSnapshot: number
  /** Huidig min peilsaldo: wat er na het tellen geboekt is (telt niet mee in de correctie). */
  mutatiesSindsTellen: number
}

export function berekenTellingVerschil(input: TellingVerschilInput): TellingVerschil {
  const peilSaldo = input.geteld != null && input.saldoBijTellen != null ? input.saldoBijTellen : input.huidigSaldo
  const mutatiesSindsSnapshot = peilSaldo - input.systeemSnapshot
  const saldoGewijzigdSindsSnapshot = mutatiesSindsSnapshot !== 0
  const mutatiesSindsTellen = input.huidigSaldo - peilSaldo
  if (input.geteld == null) {
    return {
      geteld: false,
      verschil: null,
      correctieAantal: null,
      peilSaldo,
      saldoGewijzigdSindsSnapshot,
      mutatiesSindsSnapshot,
      mutatiesSindsTellen,
    }
  }
  return {
    geteld: true,
    verschil: input.geteld - input.systeemSnapshot,
    correctieAantal: input.geteld - peilSaldo,
    peilSaldo,
    saldoGewijzigdSindsSnapshot,
    mutatiesSindsSnapshot,
    mutatiesSindsTellen,
  }
}

// ─── Vier ogen ──────────────────────────────────────────────────────────────

export interface VierOgenInstelling {
  /** Drempel waarde-effect (decimaal-string, euro). */
  bedrag: string
  /** Drempel verschil als percentage van het saldo (bij tellen). */
  percentage: number
  /** De percentage-regel geldt pas vanaf dit saldo. */
  minSysteem: number
}

export interface VierOgenOordeel {
  vereist: boolean
  /** Leesbare redenen (NL, geen em-dash), voor de UI en de audit. */
  redenen: string[]
  /** Waarde-effect correctie x prijs (decimaal-string, met teken); null zonder prijs. */
  waardeEffect: string | null
}

/**
 * Vraagt deze correctie een tweede goedkeuring (spec §C13)? Ja als het
 * waarde-effect |correctie x prijs| op of boven de drempel ligt, of als het
 * verschil op of boven het percentage van het saldo ligt (alleen bij een saldo
 * vanaf `minSysteem`). De caller geeft als `systeemAantal` het saldo bij tellen
 * (anders de snapshot): daartegen rekent ook de correctie. Zonder prijs valt de check terug op de
 * percentage-regel (§6.7). Een correctie van 0 vraagt nooit iets.
 */
export function vereistVierOgen(
  input: { correctieAantal: number; prijs: string | null; systeemAantal: number },
  instelling: VierOgenInstelling,
): VierOgenOordeel {
  if (!Number.isInteger(input.correctieAantal)) throw new Error(`Correctie moet heel zijn: ${input.correctieAantal}`)
  const abs = Math.abs(input.correctieAantal)
  const waardeEffect = input.prijs != null ? vermenigvuldigGeld(input.prijs, input.correctieAantal) : null
  if (abs === 0) return { vereist: false, redenen: [], waardeEffect }
  const redenen: string[] = []
  if (input.prijs != null) {
    const absWaarde = vermenigvuldigGeld(input.prijs, abs)
    if (geldNaarEenheden(instelling.bedrag) >= BigInt(0) && vergelijkGeld(absWaarde, instelling.bedrag) >= 0) {
      redenen.push(`Waarde-effect ${formatEuroString(absWaarde)} op of boven de drempel van ${formatEuroString(instelling.bedrag)}`)
    }
  }
  if (input.systeemAantal >= instelling.minSysteem && input.systeemAantal > 0 && abs * 100 >= instelling.percentage * input.systeemAantal) {
    const pct = Math.round((abs * 1000) / input.systeemAantal) / 10
    redenen.push(
      `Verschil van ${abs} is ${String(pct).replace('.', ',')}% van het saldo (drempel ${instelling.percentage}% vanaf ${instelling.minSysteem} stuks)`,
    )
  }
  return { vereist: redenen.length > 0, redenen, waardeEffect }
}

// ─── Looproute ──────────────────────────────────────────────────────────────

export interface LooprouteRegel {
  locatieNaam: string | null
  stelling: number | null
  plank: number | null
  vak: number | null
  artikelNummer: string
  /** Serienummer / asset-tag (uniek), als laatste sleutel. */
  identificatie?: string | null
}

/**
 * De tellijst-volgorde (zoals de A4-tellijst): locatie, dan stelling, plank en
 * vak (zonder vaklocatie achteraan, `compareVaklocatie`), dan artikelnummer en
 * identificatie. Een regel zonder locatie (uitgegeven exemplaar) komt achteraan.
 */
export function sorteerLooproute<T extends LooprouteRegel>(regels: readonly T[]): T[] {
  return [...regels].sort((a, b) => {
    if (a.locatieNaam !== b.locatieNaam) {
      if (a.locatieNaam == null) return 1
      if (b.locatieNaam == null) return -1
      const loc = a.locatieNaam.localeCompare(b.locatieNaam, 'nl')
      if (loc !== 0) return loc
    }
    const vak = compareVaklocatie(a, b)
    if (vak !== 0) return vak
    const nr = a.artikelNummer.localeCompare(b.artikelNummer, 'nl')
    if (nr !== 0) return nr
    return (a.identificatie ?? '').localeCompare(b.identificatie ?? '', 'nl')
  })
}

// ─── Nummering ──────────────────────────────────────────────────────────────

/** "TEL-2026-0001" uit het jaar en het hoogste volgnummer tot nu toe. */
export function buildTellingNummer(jaar: number, hoogsteN: number): string {
  return `TEL-${jaar}-${String(hoogsteN + 1).padStart(4, '0')}`
}

/** Volgnummer uit "TEL-2026-0012"; 0 als het niet matcht. */
export function parseTellingNummer(code: string): number {
  const match = code.match(/^TEL-\d{4}-(\d+)$/)
  if (!match) return 0
  const n = Number.parseInt(match[1] ?? '0', 10)
  return Number.isFinite(n) ? n : 0
}
